import { useState } from 'react';
import { BookMarked, CheckCircle2 } from 'lucide-react';
import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, EmptyState } from '@/components/ui';
import { MANDATORY_COURSES } from '@/lib/constants';

export function CoursesView() {
  const { activeClass, activeClassAgents, updateCourseCompletion, userRole } = useStore();
  const [selectedAgentId, setSelectedAgentId] = useState(activeClassAgents[0]?.id || '');

  const isReadOnly = userRole === 'viewer' || userRole === 'guest' || userRole === 'manager';
  const selectedAgent = activeClassAgents.find((a) => a.id === selectedAgentId);

  const handleToggle = (courseId: string, currentStatus: boolean) => {
    if (isReadOnly) {
      alert('Guest / Manager accounts have read-only access.');
      return;
    }
    if (!selectedAgentId) return;
    updateCourseCompletion(selectedAgentId, courseId, !currentStatus);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionTitle
        title="Mandatory Courses Tracker"
        subtitle="Monitor completion of required compliance and technical training modules."
        icon={<BookMarked className="w-6 h-6" />}
      />

      {!activeClass || activeClassAgents.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <EmptyState message="No active class or trainees found. Please add trainees to the roster first." />
        </Card>
      ) : (
        <div className="space-y-6">
          <Card className="p-6">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Select Trainee</label>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="w-full md:w-80 rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 bg-white font-medium focus:border-primary-500 focus:outline-none"
            >
              {activeClassAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.pNumber})
                </option>
              ))}
            </select>
          </Card>

          {selectedAgent && (
            <Card className="p-6 space-y-4">
              <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                Mandatory Compliance & System Modules
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {MANDATORY_COURSES.map((course) => {
                  const record = selectedAgent.courses?.find((c) => c.courseId === course.id);
                  const isCompleted = record?.completed ?? false;

                  return (
                    <div
                      key={course.id}
                      className={`p-4 rounded-xl border transition-all flex items-start justify-between gap-3 ${
                        isCompleted
                          ? 'bg-emerald-50/60 border-emerald-200'
                          : 'bg-slate-50/80 border-slate-200'
                      }`}
                    >
                      <div className="space-y-1">
                        <div className="font-bold text-slate-900 text-xs">{course.title}</div>
                        <div className="text-[11px] text-slate-500">{course.description || 'Required onboarding course.'}</div>
                        {isCompleted && record?.completionDate && (
                          <div className="text-[10px] font-mono text-emerald-700 font-semibold pt-1">
                            Completed on: {record.completionDate}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleToggle(course.id, isCompleted)}
                        disabled={isReadOnly}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                          isReadOnly
                            ? 'cursor-not-allowed opacity-80'
                            : 'cursor-pointer'
                        } ${
                          isCompleted
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                        }`}
                      >
                        {isCompleted ? 'Completed' : 'Mark Complete'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
